import { type FC, type ReactNode, useRef } from "react";
import { Field, Form } from "react-final-form";
import Grid from "@mui/material/Grid";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormGroup,
  IconButton,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DateTimePicker as MuiDateTimePicker } from "@mui/x-date-pickers";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import { DateTimePicker, Switch } from "@/shared/components/Form";
import { ShareUrl } from "@/shared/components/ShareUrl";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import { useCurrentPlatformQuery } from "@/partner/entities/platforms";
import {
  ProductStatusType,
  ProductTypeType,
  type CreateProductInputType,
  type UpdateProductInputType,
} from "@/types/graphql/partner.generated";
import {
  DEFAULT_PRODUCT_FORM_VALUES,
  buildProductInput,
  buildUpdateProductInput,
  getMinPricesDateFrom,
  getMissingRequiredFields,
  type ProductFormValues,
  type ProductRequiredField,
} from "../model";
import { ProductTypeToggle } from "./ProductTypeToggle";
import { PricesField } from "./PricesField";
import { ProductPreviewImageField } from "./ProductPreviewImageField";
import { ProductPreviewCard } from "./ProductPreviewCard";

const FORM_ID = "product-form";
const SPACING = 1;
const FORM_SPACING = 2;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * minDate для finishAt (référence: Edit.js — max(startAt+1д,
 * max(dateFrom + period дней))). Только предупреждение (helperText) — сабмит
 * не блокируется, если finishAt раньше этой даты.
 */
function getFinishAtMinDate(values: ProductFormValues): Date | null {
  if (!values.startAt) return null;

  let minDate = new Date(new Date(values.startAt).getTime() + DAY_MS);

  values.prices.forEach((price) => {
    const periodDays = price.period;
    if (!periodDays) return;
    price.periods.forEach((period) => {
      if (!period.dateFrom) return;
      const end = new Date(
        new Date(period.dateFrom).getTime() + periodDays * DAY_MS
      );
      if (end > minDate) minDate = end;
    });
  });

  return minDate;
}

// Подписи незаполненных обязательных полей для подсказки над формой и на
// кнопках шапки; где есть, переиспользуем подписи самих полей.
const REQUIRED_FIELD_LABEL_KEYS: Record<ProductRequiredField, string> = {
  title: "products.form.titleLabel",
  description: "products.form.descriptionLabel",
  dates: "products.form.requiredDates",
  preview: "products.form.requiredPreview",
  price: "products.form.requiredPrice",
};

interface ProductFormMetadata {
  siteUrl?: string | null;
}

interface ProductFormProps {
  title: string;
  loading?: boolean;
  /** Тип продукта можно выбрать только при создании — на редактировании
   * ProductType неизменяем и его тумблер вовсе не рендерится (référence:
   * Edit.js `{!isEdit && <ProductType .../>}`). */
  isEdit?: boolean;
  /** Черновик ли продукт изначально (`!initialValues.publishAt`) — определяет
   * подписи кнопок шапки. Для создания всегда true. */
  isDraft?: boolean;
  initialValues?: Partial<ProductFormValues>;
  /** На создании — CreateProductInputType (с `type`); на редактировании —
   * UpdateProductInputType (без `type` — Product.type immutable, см. C1 в
   * финальном ревью продуктов). */
  onSubmit: (
    input: CreateProductInputType | UpdateProductInputType
  ) => Promise<void>;
  onClose: () => void;
  metadata?: ProductFormMetadata;
  keepDirtyOnReinitialize?: boolean;
  /** Статус продукта при редактировании — только для строки статуса в шапке
   * (référence: Edit.js statusesDesc). При создании не передаётся. */
  status?: ProductStatusType;
  /** Сообщение ошибки последней мутации создания/сохранения (référence:
   * Edit.js `{error && <ErrorMessage error={error} />}`). */
  error?: string | null;
  /** Меню действий в шапке (ProductActionsMenu), справа от кнопок. */
  headerMenu?: ReactNode;
  /** Блок над формой (ход отмены события). */
  notice?: ReactNode;
}

export const ProductForm: FC<ProductFormProps> = ({
  title,
  loading = false,
  isEdit = false,
  isDraft = true,
  initialValues,
  onSubmit,
  onClose,
  metadata,
  keepDirtyOnReinitialize,
  status,
  error,
  headerMenu,
  notice,
}) => {
  const { t } = useTranslation();
  // Продукт можно завести без платёжного провайдера (для демо и до
  // подключения Stripe), но покупки не пройдут: предупреждаем прямо над
  // ценами. paymentSettings у платформы null, пока не подключён ни один
  // провайдер (см. Platform.paymentSettings в partner-схеме).
  const { data: platformData, loading: platformLoading } =
    useCurrentPlatformQuery();
  const paymentsNotConnected =
    !platformLoading && !platformData?.currentPlatform?.paymentSettings;
  // référence: Edit.js использует useIsMobile() без аргумента — дефолтный
  // брейкпоинт партнёрского хука "md" (=breakpoints.down("md"), 900px), а НЕ
  // 600px — тот же порог управляет и column-reverse грида, и размером кнопок
  // шапки (аудит, пп.13-14).
  const isMobile = useDeviceBreakpoint("md");
  // true = «Опубликовать»/«Сохранить», false = «Черновик»/«Скрыть» — как в
  // VideoForm, устанавливается синхронно в onClick одной из кнопок перед
  // сабмитом.
  const publishIntentRef = useRef(!isDraft);

  const handleFormSubmit = async (values: ProductFormValues) => {
    const publish = publishIntentRef.current;
    const minDateFrom = getMinPricesDateFrom(values.prices);
    const publishAt = publish ? minDateFrom || new Date().toISOString() : null;
    // Тип продукта неизменяем — на редактировании инпут строится БЕЗ `type`
    // (UpdateProductInput его не содержит), иначе GraphQL отклоняет весь
    // запрос на каждое сохранение формы (см. C1 в финальном ревью).
    const input = isEdit
      ? buildUpdateProductInput(values, publishAt)
      : buildProductInput(values, publishAt);
    await onSubmit(input);
  };

  const mergedInitialValues: ProductFormValues = {
    ...DEFAULT_PRODUCT_FORM_VALUES,
    ...initialValues,
  };

  return (
    <Form<ProductFormValues>
      onSubmit={handleFormSubmit}
      initialValues={mergedInitialValues}
      keepDirtyOnReinitialize={keepDirtyOnReinitialize}
      subscription={{ values: true }}
      render={({ handleSubmit, values = mergedInitialValues, form }) => {
        const computedPublishAt = getMinPricesDateFrom(values.prices);
        const isScheduled = Boolean(
          computedPublishAt && new Date(computedPublishAt) > new Date()
        );
        const missingFields = getMissingRequiredFields(values);
        const canPublish = missingFields.length === 0;
        // Обе кнопки шапки неактивны, пока не заполнены обязательные поля:
        // объясняем почему в тултипе и в Alert над формой (на мобильном
        // тултипов нет).
        const missingFieldsHint = canPublish
          ? null
          : t("products.form.requiredHint", {
              fields: missingFields
                .map((field) => t(REQUIRED_FIELD_LABEL_KEYS[field]))
                .join(", "),
            });
        const finishAtMinDate = getFinishAtMinDate(values);
        const finishAtHelperText =
          finishAtMinDate &&
          values.finishAt &&
          new Date(values.finishAt) < finishAtMinDate
            ? t("products.form.finishAtWarning", { date: finishAtMinDate })
            : undefined;

        return (
          <form id={FORM_ID} onSubmit={handleSubmit}>
            {/* référence: DialogHeader.js — zIndex 1399 (выше стандартного
                AppBar-дефолта, но ниже MUI Modal/Dialog — здесь форма сама
                fullScreen, а не внутри отдельного Dialog). */}
            <AppBar
              color="default"
              position="fixed"
              elevation={0}
              sx={{ zIndex: 1399 }}
            >
              <Toolbar>
                <IconButton edge="start" onClick={onClose} sx={{ mr: 1 }}>
                  <CloseIcon />
                </IconButton>
                <Typography variant="h6" noWrap sx={{ flex: 1, mr: 1 }}>
                  {title}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  {/* référence: Edit.js:176-181 — статус продукта в шапке
                      формы редактирования, только на десктопе. */}
                  {isEdit && !isMobile && status && (
                    <Typography color="textSecondary" variant="subtitle1">
                      {status === ProductStatusType.PublishedType &&
                        t("products.form.statusPublished")}
                      {status === ProductStatusType.DraftType &&
                        t("products.form.statusDraft")}
                      {status === ProductStatusType.FinishedType &&
                        t("products.form.statusFinished")}
                    </Typography>
                  )}
                  <Tooltip
                    title={
                      missingFieldsHint ??
                      (isScheduled && computedPublishAt
                        ? t("products.form.publishScheduledTooltip", {
                            date: computedPublishAt,
                          })
                        : isDraft
                          ? t("products.form.publishTooltip")
                          : t("products.form.saveChangesTooltip"))
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        type="submit"
                        form={FORM_ID}
                        disabled={loading || !canPublish}
                        disableElevation
                        size={isMobile ? "small" : "medium"}
                        sx={{ fontSize: isMobile ? "0.7rem" : undefined }}
                        onClick={() => {
                          publishIntentRef.current = true;
                        }}
                      >
                        {isDraft
                          ? t("common.actions.publish")
                          : t("common.actions.save")}
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      missingFieldsHint ??
                      (isDraft
                        ? t("products.form.saveDraftTooltip")
                        : t("products.form.hideTooltip"))
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        type="submit"
                        form={FORM_ID}
                        disabled={loading || !canPublish}
                        disableElevation
                        size={isMobile ? "small" : "medium"}
                        sx={{ fontSize: isMobile ? "0.7rem" : undefined }}
                        onClick={() => {
                          publishIntentRef.current = false;
                        }}
                      >
                        {/* référence: Edit.js:206 — isDraft ? save : draft
                            (не «Сохранить черновик»/«Скрыть» — это разные
                            подписи из другого состояния). */}
                        {isDraft
                          ? t("common.actions.save")
                          : t("products.form.draftButton")}
                      </Button>
                    </span>
                  </Tooltip>
                  {headerMenu}
                </Stack>
              </Toolbar>
              <Divider />
            </AppBar>

            <Box sx={{ p: 1, mt: { xs: "56px", sm: "64px" } }}>
              {missingFieldsHint && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  {missingFieldsHint}
                </Alert>
              )}
              {notice}
              {error && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {error}
                </Alert>
              )}
              <Grid
                container
                spacing={SPACING}
                flexDirection={isMobile ? "column-reverse" : "row"}
              >
                <Grid size={{ xs: 12, md: 8 }}>
                  <Grid container spacing={SPACING}>
                    <Grid size={12}>
                      <Card elevation={0}>
                        <CardContent sx={{ pt: 3 }}>
                          <Grid container spacing={FORM_SPACING}>
                            <Grid size={12}>
                              <Field name="title">
                                {({ input, meta }) => (
                                  <TextField
                                    {...input}
                                    autoFocus
                                    required
                                    label={t("products.form.titleLabel")}
                                    fullWidth
                                    variant="outlined"
                                    error={meta.touched && Boolean(meta.error)}
                                  />
                                )}
                              </Field>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                              {/* référence: Edit.js:273-282 — очистка
                                  eventStartAt обнуляет eventFinishAt.
                                  Собственный Field вместо shared
                                  DateTimePicker — тому нечем перехватить
                                  onChange для побочного эффекта на другое
                                  поле формы. */}
                              <Field name="eventStartAt">
                                {({ input }) => (
                                  <MuiDateTimePicker
                                    label={t("products.form.eventStartAtLabel")}
                                    ampm={false}
                                    value={
                                      input.value ? moment(input.value) : null
                                    }
                                    onChange={(value) => {
                                      const iso = value
                                        ? value.toISOString()
                                        : null;
                                      input.onChange(iso);
                                      if (!iso) {
                                        form.change("eventFinishAt", null);
                                      }
                                    }}
                                    slotProps={{
                                      textField: { fullWidth: true },
                                    }}
                                  />
                                )}
                              </Field>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                              {/* référence: Edit.js:284-299 — disabled без
                                  eventStartAt, minDate=eventStartAt. */}
                              <Field name="eventFinishAt">
                                {({ input }) => (
                                  <MuiDateTimePicker
                                    label={t(
                                      "products.form.eventFinishAtLabel"
                                    )}
                                    ampm={false}
                                    value={
                                      input.value ? moment(input.value) : null
                                    }
                                    onChange={(value) =>
                                      input.onChange(
                                        value ? value.toISOString() : null
                                      )
                                    }
                                    disabled={!values.eventStartAt}
                                    minDate={
                                      values.eventStartAt
                                        ? moment(values.eventStartAt)
                                        : undefined
                                    }
                                    slotProps={{
                                      textField: { fullWidth: true },
                                    }}
                                  />
                                )}
                              </Field>
                            </Grid>
                            <Grid size={12}>
                              <Field name="description">
                                {({ input }) => (
                                  <TextField
                                    {...input}
                                    required
                                    label={t("products.form.descriptionLabel")}
                                    multiline
                                    minRows={2}
                                    maxRows={10}
                                    fullWidth
                                    variant="outlined"
                                  />
                                )}
                              </Field>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid size={12}>
                      <Card elevation={0}>
                        {/* référence: Edit.js:354-365 — PricesField НЕ
                            оборачивается в CardContent снаружи (список цен
                            должен быть full-bleed, отступ только вокруг
                            кнопки «Добавить цену» — см. PricesField.tsx). */}
                        <Grid container spacing={1} alignItems="center">
                          {!isEdit && (
                            <Grid size={12}>
                              <Field name="type">
                                {({ input }) => (
                                  <ProductTypeToggle
                                    value={input.value}
                                    onChange={(newType) => {
                                      input.onChange(newType);
                                      // Смена типа сбрасывает prices
                                      // (référence: ProductForm.js
                                      // onChangeProductType).
                                      form.change("prices", []);
                                    }}
                                  />
                                )}
                              </Field>
                            </Grid>
                          )}
                          {paymentsNotConnected && (
                            <Grid size={12}>
                              <Alert severity="warning">
                                {t("products.form.paymentsNotConnected")}
                              </Alert>
                            </Grid>
                          )}
                          <Grid size={12}>
                            <PricesField
                              productType={values.type}
                              defaultDateFrom={computedPublishAt}
                            />
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>

                    <Grid size={12}>
                      <Card elevation={0}>
                        <CardContent sx={{ pt: 3 }}>
                          <Grid container spacing={FORM_SPACING}>
                            <Grid size={{ xs: 12, sm: 4 }}>
                              <DateTimePicker
                                name="startAt"
                                required
                                label={t("products.form.startAtLabel")}
                                outputFormat="iso"
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                              <DateTimePicker
                                name="finishAt"
                                required
                                label={t("products.form.finishAtLabel")}
                                outputFormat="iso"
                                minDate={finishAtMinDate ?? undefined}
                                helperText={finishAtHelperText}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                              <DateTimePicker
                                name="finishSalesAt"
                                label={t("products.form.finishSalesAtLabel")}
                                outputFormat="iso"
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid size={12}>
                      <Card elevation={0}>
                        <CardContent sx={{ pt: 3 }}>
                          <Grid container spacing={FORM_SPACING}>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <ProductPreviewImageField />
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid size={12}>
                      {/* référence: Edit.js:639-640 — единственная карточка
                          формы БЕЗ elevation={0} (с тенью, как остальные
                          обычные Card в приложении). */}
                      <Card>
                        <CardContent>
                          <FormControl component="fieldset">
                            <FormGroup>
                              <Switch
                                name="showContent"
                                label={t("products.form.showContentLabel")}
                              />
                              <Switch
                                name="hideInGeneralLists"
                                label={t(
                                  "products.form.hideInGeneralListsLabel"
                                )}
                              />
                              {values.type === ProductTypeType.PpvType && (
                                <Tooltip
                                  title={t(
                                    "products.form.perContentPurchaseTooltip"
                                  )}
                                >
                                  <span>
                                    <Switch
                                      name="perContentPurchase"
                                      label={t(
                                        "products.form.perContentPurchaseLabel"
                                      )}
                                    />
                                  </span>
                                </Tooltip>
                              )}
                            </FormGroup>
                          </FormControl>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Stack spacing={SPACING}>
                    <ProductPreviewCard
                      title={values.title}
                      previewId={values.previewId}
                      eventStartAt={values.eventStartAt}
                      eventFinishAt={values.eventFinishAt}
                      prices={values.prices}
                    />
                    {isEdit && metadata?.siteUrl && (
                      <Card elevation={0}>
                        <CardContent>
                          <ShareUrl
                            url={metadata.siteUrl}
                            label={t("products.form.shareUrlLabel")}
                            copiedMessage={t("products.form.shareUrlCopied")}
                          />
                        </CardContent>
                      </Card>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </form>
        );
      }}
    />
  );
};
